/**
 * The client-side half of the 90-day back-capture rule.
 *
 * The SERVER is the authority: backend/src/utils/backCaptureWindow.js's
 * beyondCheck throws BACK_CAPTURE_TOO_OLD and nothing is written. This module
 * exists so the UI can say so BEFORE the click rather than round-tripping to
 * a predictable 400, and is shared by the foliar Record modal (#482) and the
 * soil Record panel so the two surfaces cannot disagree about where the
 * boundary is.
 *
 * Mirrors backend/src/utils/backCaptureWindow.js's BACK_CAPTURE_BEYOND_MS.
 */
export const BACK_CAPTURE_BEYOND_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * True when `dateValue` is older than the window.
 *
 * Returns FALSE for an empty or unparseable value. "You haven't entered a date"
 * and "your date is too old" are different problems with different remedies, and
 * an unparseable value must not present to the user as the latter. The caller's
 * own required-field check handles the empty case.
 *
 * `now` is injectable so tests cannot drift with the clock.
 */
export function isBeyondBackCaptureWindow(dateValue: string, now: Date = new Date()): boolean {
  if (!dateValue) return false;
  const ms = new Date(dateValue).getTime();
  if (Number.isNaN(ms)) return false;
  return ms < now.getTime() - BACK_CAPTURE_BEYOND_MS;
}

/** Beyond the window AND the tenant override is off — i.e. the server will refuse. */
export function isBackCaptureBlocked(
  dateValue: string, overrideOn: boolean, now: Date = new Date(),
): boolean {
  return isBeyondBackCaptureWindow(dateValue, now) && !overrideOn;
}
